import { useEffect, useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash2, AlertTriangle, CheckCircle2 } from "lucide-react";
import {
  REVIEW_REASON_OPTIONS, REVIEW_CONFIDENCE_OPTIONS,
} from "./ReviewIntelligencePrompt";

const VAULTS = [
  { key: "TAX_CUSTODY", label: "Tax Custody" },
  { key: "SUPPLIER_OBLIGATION", label: "Supplier Obligation" },
  { key: "CUSTOMER_TRUST", label: "Customer Trust" },
  { key: "INVENTORY_CUSTODY", label: "Inventory Custody" },
  { key: "OPERATING_CUSTODY", label: "Operating Custody" },
  { key: "FREE_EQUITY", label: "Free Equity" },
  // Expansion 4 — debt, reserves, strategic capital
  { key: "CREDIT_FACILITY_CUSTODY", label: "Credit Facility" },
  { key: "LOAN_OBLIGATION", label: "Loan Obligation" },
  { key: "RESERVE_CAPITAL", label: "Reserve Capital" },
  { key: "STRATEGIC_CAPITAL", label: "Strategic Capital" },
];

/**
 * @param {{ open: boolean, record: any, onClose: () => void, onSave: (payload: any) => Promise<void> }} props
 */
export default function ManualAllocateModal({ open, record, onClose, onSave }) {
  const [allocations, setAllocations] = useState([]);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  // P3 — Review Intelligence (required)
  const [reasonCode, setReasonCode] = useState("");
  const [userConfidence, setUserConfidence] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [saving, setSaving] = useState(false);

  const np = record?.normalized_payload || {};
  const target = np.reporting_amount || 0;
  const currency = np.reporting_currency || "GBP";

  useEffect(() => {
    if (record) {
      const current = record.final_allocation || record.ai_result?.recommended_vault_allocations || [];
      setAllocations(current.length > 0 ? current.map((a) => ({ ...a })) : [
        { vault: "FREE_EQUITY", source_amount: np.source_amount || 0, source_currency: np.source_currency || "GBP",
          reporting_amount: target, reporting_currency: currency, reason_code: "manual_allocation" },
      ]);
      setReason("");
      setNote("");
      setReasonCode("");
      setUserConfidence("");
      setReasonText("");
    }
  }, [record]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = useMemo(
    () => allocations.reduce((s, a) => s + (parseFloat(a.reporting_amount) || 0), 0),
    [allocations]
  );
  const diff = +(total - target).toFixed(2);
  // Reason must be ≥10 chars (matches server-side validation). The
  // brief: "manual override is sticky, so the caller MUST justify it."
  const REASON_MIN = 10;
  const reasonValid = reason.trim().length >= REASON_MIN;
  const reviewMetaValid = Boolean(reasonCode) && Boolean(userConfidence);
  const valid = Math.abs(diff) <= 0.02 && reasonValid && reviewMetaValid && allocations.every(a => a.vault && a.reporting_amount != null);

  const updateRow = (i, field, value) => {
    setAllocations((prev) =>
      prev.map((a, idx) => (idx === i ? { ...a, [field]: field.includes("amount") ? parseFloat(value) || 0 : value } : a))
    );
  };
  const addRow = () =>
    setAllocations((prev) => [
      ...prev,
      { vault: "FREE_EQUITY", source_amount: 0, source_currency: np.source_currency || "GBP",
        reporting_amount: 0, reporting_currency: currency, reason_code: "manual_split" },
    ]);
  const removeRow = (i) => setAllocations((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      await onSave({
        allocations,
        override_reason: reason,
        note,
        reason_code: reasonCode,
        user_confidence: userConfidence,
        reason_text: reasonText.trim() || undefined,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!record) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0B1120] border-slate-800 max-w-xl max-h-[85vh] overflow-y-auto" data-testid="manual-allocate-modal">
        <DialogHeader>
          <DialogTitle className="font-barlow text-slate-50">Manual Vault Allocation</DialogTitle>
          <DialogDescription className="text-slate-500">
            Override the AI&apos;s allocation. Allocations must sum to {currency} {target.toLocaleString(undefined, { minimumFractionDigits: 2 })}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <div className="text-xs text-slate-300">{np.description}</div>
            <div className="text-[10px] text-slate-500 font-mono mt-0.5">{np.counterparty || "Unknown"} · {np.direction}</div>
          </div>

          <div className="space-y-2">
            {allocations.map((a, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`alloc-row-${i}`}>
                <Select value={a.vault} onValueChange={(v) => updateRow(i, "vault", v)}>
                  <SelectTrigger className="bg-slate-900/50 border-slate-700 text-xs flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {VAULTS.map((v) => <SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" step="0.01" value={a.reporting_amount}
                  onChange={(e) => updateRow(i, "reporting_amount", e.target.value)}
                  className="w-32 bg-slate-900/50 border-slate-700 text-xs font-mono"
                  data-testid={`alloc-amount-${i}`} />
                {allocations.length > 1 && (
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-600 hover:text-red-400"
                    onClick={() => removeRow(i)} data-testid={`alloc-remove-${i}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            ))}
            <Button size="sm" variant="ghost" className="h-7 text-[11px] text-cyan-400" onClick={addRow} data-testid="alloc-add-row">
              <Plus className="w-3 h-3 mr-1" /> Add split row
            </Button>
          </div>

          <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-800/30 text-xs">
            <span className="text-slate-400">Sum of allocations</span>
            <span className={`font-mono font-semibold ${Math.abs(diff) <= 0.02 ? "text-emerald-400" : "text-red-400"}`}
              data-testid="alloc-sum">
              {currency} {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              {Math.abs(diff) > 0.02 && <span className="ml-2 text-red-500/80">(off by {diff.toFixed(2)})</span>}
            </span>
          </div>
          {Math.abs(diff) <= 0.02
            ? <div className="flex items-center gap-2 text-[11px] text-emerald-400"><CheckCircle2 className="w-3 h-3" /> Allocation reconciled</div>
            : <div className="flex items-center gap-2 text-[11px] text-amber-400"><AlertTriangle className="w-3 h-3" /> Adjust amounts so they sum to the record total</div>
          }

          <div>
            <Label className="text-xs text-slate-400">
              Reason for manual allocation
              <span className="ml-2 text-[10px] text-amber-400">required, min {REASON_MIN} chars</span>
            </Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you overriding the AI? This will be recorded permanently and the record will be locked from future re-classification."
              className="bg-slate-900/50 border-slate-700 mt-1 min-h-[60px] text-xs"
              data-testid="alloc-reason" />
            <div className="flex items-center justify-between mt-1">
              <span className={`text-[10px] ${reasonValid ? "text-emerald-400" : "text-slate-500"}`} data-testid="alloc-reason-counter">
                {reason.trim().length} / {REASON_MIN} {reasonValid ? "✓" : ""}
              </span>
              <span className="text-[10px] text-slate-500">🔒 Saving will lock this record</span>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Internal note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
              className="bg-slate-900/50 border-slate-700 mt-1 text-xs" data-testid="alloc-note" />
          </div>

          {/* P3 Review Intelligence — required */}
          <div data-testid="alloc-review-reason-field">
            <Label className="text-xs text-slate-400">
              Why are you changing this?
              <span className="ml-2 text-[10px] text-amber-400">required</span>
            </Label>
            <Select value={reasonCode} onValueChange={setReasonCode}>
              <SelectTrigger
                className="bg-slate-900/50 border-slate-700 text-xs mt-1"
                data-testid="alloc-review-reason-trigger"
              >
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                {REVIEW_REASON_OPTIONS.map((r) => (
                  <SelectItem
                    key={r.value} value={r.value}
                    data-testid={`alloc-review-reason-option-${r.value}`}
                  >
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div data-testid="alloc-review-confidence-field">
            <Label className="text-xs text-slate-400">
              How confident are you?
              <span className="ml-2 text-[10px] text-amber-400">required</span>
            </Label>
            <div
              className="grid grid-cols-3 gap-2 mt-1.5"
              role="radiogroup"
              aria-label="Reviewer confidence"
            >
              {REVIEW_CONFIDENCE_OPTIONS.map((c) => {
                const active = userConfidence === c.value;
                return (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setUserConfidence(c.value)}
                    aria-checked={active}
                    role="radio"
                    data-testid={`alloc-review-confidence-${c.value}`}
                    className={[
                      "text-[11px] font-semibold py-2 rounded-md border transition-colors",
                      active
                        ? "border-cyan-400 bg-cyan-500/15 text-cyan-300"
                        : "border-slate-700 bg-slate-900/50 text-slate-400 hover:border-slate-600 hover:text-slate-200",
                    ].join(" ")}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div data-testid="alloc-review-note-field">
            <Label className="text-xs text-slate-400">
              Reviewer note (optional)
              <span className="ml-2 text-[10px] text-slate-500">
                helps Sentient Vault learn faster
              </span>
            </Label>
            <Textarea
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value.slice(0, 1000))}
              placeholder="Short context for future learning…"
              className="bg-slate-900/50 border-slate-700 mt-1 min-h-[50px] text-xs"
              data-testid="alloc-review-note-input"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-slate-700" data-testid="alloc-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={!valid || saving} className="bg-amber-600 hover:bg-amber-500 text-slate-900"
            data-testid="alloc-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Saving...</> : "Save Allocation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
